import PropTypes from 'prop-types';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { shouldTranslateByDefault } from '@icp/settings';
import clsx from 'clsx';
import { HELPER_TEXT_TYPES, helpersIsEmpty } from '@icp/form-renderer-core';
import {
  DEFAULT_MAPPING,
  DEFAULT_VALUE_TYPE,
  formatOptions,
  toComponentValue,
  toInterfaceValue,
} from '../SelectElement/utils';
import {
  useClassName,
  useDataSourceSupportLazy,
  useHttpBody,
  useVariablePattern,
  useVariablePatternArray,
} from '../../hooks';
import { useElementDecorator, useIsInDesign } from '../../FormRenderCtx';
import FieldTitle from '../../FieldTitle';
import { ConditionalPropertyPropType, SingleDataSourceTypes } from '../../propTypes';
import { getDisplayText } from '../CascaderElement/utils';
import ListSelect from './ListSelect';
import { withFieldWrapper } from '../../fieldWrapper';
import FormHelperText from '../../FormHelperText';
import RecursionRenderer from '../../RecursionRenderer/RecursionRenderer';
import { CurrentDataProvider } from '../../currentDataCtx';

const EMPTY_DATA = [{}];

const ListSelectElement = forwardRef(function ListSelectElement(props, ref) {
  const {
    children,
    fields = [],
    keyPath,
    // id,
    className: classNameProp,
    title,
    value: valueProp,
    disabled,
    style,
    componentProps = {},
    fieldTitleProps,
    validation,
    readonly,
    // status,
    helpers,
    onChange,
    onTouchChanged,
  } = props;

  const {
    itemProps = {},
    selectedItemProps = {},
    showCheckMark = false,
    multiple = false,
    valueType = DEFAULT_VALUE_TYPE,
    mapping = DEFAULT_MAPPING,
    useOriginValue = false,
    stringEqual = false,
    options: optionsProp,
    dataSource,
    dataUrl,
    dataUrlResolveMode,
    dataFilters,
    sortModel,
    dataResponseKeyPath = 'results',
    transformDataResponse,
    translateDataResponse = shouldTranslateByDefault(),
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody: httpBodyProp,
    selectColId,
    needCount = false,
    dataExclusion: dataExclusionProp,
    searchText: searchTextProp,
    // lazyLoading = false,
    // cacheBlockSize = 30,
    ...otherComponentProps
  } = componentProps;

  const ElementDecorator = useElementDecorator();
  const isInDesign = useIsInDesign();

  const className = useClassName(classNameProp);
  const classNameComp = useClassName(componentProps.className);
  const dataExclusion = useVariablePatternArray(dataExclusionProp);

  const searchText = useVariablePattern(searchTextProp) || undefined;

  const [refresh, setRefresh] = useState({});

  const nodeRef = useRef(null);

  const httpBody = useHttpBody(httpBodyProp);

  const { loading, dataFetched } = useDataSourceSupportLazy({
    skip: isInDesign || !!optionsProp,
    refresh,
    lazy: false, // ListSelect 暂时不支持懒加载
    searchText,
    debounceLeading: !searchText,
    // cacheBlockSize,
    // single data source config
    dataSource,
    dataUrl,
    dataResponseKeyPath,
    dataFilters,
    sortModel,
    transformDataResponse,
    translateDataResponse,
    debounceTime,
    defaultValue,
    httpMethod,
    httpBody,
    selectColId,
    needCount,
    dataUrlResolveMode,
  });

  const options = useMemo(() => {
    if (isInDesign) {
      return Array.isArray(optionsProp) && optionsProp.length ? optionsProp : EMPTY_DATA;
    }
    // TODO，有个无意义的性能消耗是每次懒加载都要把全部数据 format 一份
    return formatOptions({
      options: optionsProp || dataFetched,
      mapping,
      stringEqual,
      dataExclusion,
    });
  }, [isInDesign, optionsProp, dataFetched, mapping, stringEqual, dataExclusion]);

  useImperativeHandle(
    ref,
    () => ({
      node: nodeRef.current,
      getOptions: () => options,
      refresh: () => setRefresh({}),
    }),
    [options],
  );

  const innerValue = useMemo(() => {
    return toComponentValue({
      valueProp,
      options,
      multiple,
      mapping,
      useOriginValue,
      stringEqual,
    });
  }, [mapping, multiple, options, stringEqual, useOriginValue, valueProp]);

  const handleChange = (newValue) => {
    const val = toInterfaceValue({
      oldValue: valueProp,
      newValue,
      options,
      multiple,
      valueType,
      mapping,
      useOriginValue,
      stringEqual,
      isStandardDataSource: !!dataSource,
    });
    onChange(val);
    if (onTouchChanged) {
      onTouchChanged();
    }
  };

  return (
    <ElementDecorator keyPath={keyPath}>
      <div
        className={clsx(
          'list-select-element',
          'input-element',
          'form-element',
          {
            'has-helper': !helpersIsEmpty(helpers),
          },
          className,
        )}
        style={style}
        ref={nodeRef}
      >
        <FieldTitle required={validation?.required} {...fieldTitleProps}>
          {title}
        </FieldTitle>
        <div>
          {readonly ? (
            <span className="readonly-text">
              {getDisplayText({
                valueProp,
                mapping,
                useOriginValue,
                options: optionsProp || dataFetched || [],
              })}
            </span>
          ) : (
            <ListSelect
              {...otherComponentProps}
              itemProps={itemProps}
              selectedItemProps={selectedItemProps}
              className={classNameComp}
              value={innerValue}
              options={options}
              loading={loading}
              multiple={multiple}
              mapping={mapping}
              disabled={disabled}
              showCheckMark={showCheckMark}
              renderOption={
                children || fields?.length
                  ? (option) => {
                      return (
                        <CurrentDataProvider key={option[mapping.value]} value={option}>
                          {children || (
                            <RecursionRenderer fields={fields} keyPath={`${keyPath},fields`} />
                          )}
                        </CurrentDataProvider>
                      );
                    }
                  : undefined
              }
              onChange={handleChange}
            />
          )}
          <FormHelperText helpers={helpers} />
        </div>
      </div>
    </ElementDecorator>
  );
});

ListSelectElement.propTypes = {
  children: PropTypes.node,
  fields: PropTypes.arrayOf(PropTypes.shape({})),
  keyPath: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.bool,
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
    }),
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool])),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      }),
    ),
  ]),
  componentProps: PropTypes.shape({
    /**
     * 组件的 className
     */
    className: PropTypes.string,
    /**
     * 组件的 style
     */
    style: PropTypes.shape({}),
    /**
     * Describes the value is a `childEnum` item or a `childEnum` item's value,
     * describes whether the value is always an array no matter mode is single or multiple.
     *
     * example:
     *
     * Given childEnum: `[{ value: 1, label: 'AAA' }, { value: 'b', label: 'BBB' }]`
     *
     * When valueType is `value`, value would be `1` or `'b'` in `single` mode, `[1]` or `[1, 'b']` in `multiple` mode.
     *
     * When valueType is `item`, value would be `{ value: 1, label: 'AAA' }`  in `single` mode, `[{ value: 1, label: 'AAA' }, {...}]` in `multiple` mode.
     *
     * When valueType is `valueAlwaysArray`, value would be `[1]` in `single` mode, `[1, 'b']` in `multiple` mode.
     *
     * When valueType is `itemAlwaysArray`, value would be `[{ value: 1, label: 'AAA' }]` in `single` mode, `[{...}, {...}]` in `multiple` mode.
     */
    valueType: PropTypes.oneOf(['value', 'item', 'valueAlwaysArray', 'itemAlwaysArray']),
    /**
     * value 的映射关系，默认是 { value, label }
     */
    mapping: PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      // Other key map to save
    }),
    /**
     * Given mapping: `{ value: 'id', label: 'name' }`,
     * When useOriginValue is `true`, value would be `[{ id: 'xxx',name: 'xxx' }]`,
     * When useOriginValue is `false`, value would be `[{ value: 'xxx',label: xxx' }]`,
     * @default false
     */
    useOriginValue: PropTypes.bool,
    /**
     * 在比较选项的时候，是否转换成字符串进行比较
     * @default true
     */
    stringEqual: PropTypes.bool,
    /**
     * 可选项
     */
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
      }),
    ),
    ...SingleDataSourceTypes,
    /**
     * 在 api 返回的数据中排除某些数据
     */
    dataExclusion: PropTypes.arrayOf(PropTypes.string),
    /**
     * 指定模糊搜索文本
     */
    searchText: PropTypes.string,
    /**
     * 传给每个子元素顶层 div 的属性
     */
    itemProps: PropTypes.shape({}),
    /**
     * 传给选中子元素顶层 div 的属性
     */
    selectedItemProps: PropTypes.shape({}),
    /**
     * 选中项目是否显示打勾，仅在 listStyle 等于 style1 和 style1 时生效
     * @default false
     */
    showCheckMark: PropTypes.bool,
    /**
     * 选项的样式
     * @default style1
     */
    listStyle: PropTypes.oneOf(['style1', 'style2', 'style3', 'style4']),
    /**
     * 选项的尺寸，其中高度为最小高度
     * @default medium
     */
    size: PropTypes.oneOf(['medium', 'large']),
  }),
  fieldTitleProps: PropTypes.shape({
    showColon: PropTypes.bool,
  }),
  validation: PropTypes.shape({
    required: PropTypes.bool,
    minLength: PropTypes.number,
    minLengthErrorMessage: PropTypes.string,
    maxLength: PropTypes.number,
    maxLengthErrorMessage: PropTypes.string,
  }),
  disabled: ConditionalPropertyPropType(PropTypes.bool),
  readonly: ConditionalPropertyPropType(PropTypes.bool),
  status: PropTypes.oneOf(HELPER_TEXT_TYPES),
  helpers: PropTypes.arrayOf(
    PropTypes.shape({
      status: PropTypes.oneOf(HELPER_TEXT_TYPES),
      text: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func,
  onTouchChanged: PropTypes.func,
};

// for @icp/utils/getComponentDisplayName, otherwise, in production mode, function name will be compressed.
ListSelectElement.displayName = 'ListSelect';

export default withFieldWrapper(ListSelectElement);
